import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';

interface ExpandableTextProps {
    content: string;
    maxLine?: number;
}

export const ExpandableText = ({ content, maxLine = 4}: ExpandableTextProps) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const textRef = React.useRef<HTMLDivElement>(null);
    const hiddenTextRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current && hiddenTextRef.current) {
                const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
                const singleLineHeight = isNaN(lineHeight) ? 24 : lineHeight; // fallback to 24px
                const fullTextHeight = hiddenTextRef.current.scrollHeight;
                
                setHasOverflow(fullTextHeight > singleLineHeight * 1.1); // Small tolerance for rounding
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [content]);

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Hidden full text for measuring */}
            <Typography
                ref={hiddenTextRef}
                variant="body2"
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    height: 'auto',
                    width: '100%',
                    lineHeight: 1.5,
                    pointerEvents: 'none',
                    zIndex: -1,
                }}>
                {content}
            </Typography>

            <Box sx={{ position: 'relative' }}>
                <Typography
                    ref={textRef}
                    variant="body2"
                    color={theme.palette.neutralWhite.main}
                    sx={{
                        lineHeight: 1.5,
                        paddingRight: hasOverflow && !isExpanded ? '100px' : '0', // Make space for button
                        ...(!isExpanded && hasOverflow && {
                            display: '-webkit-box',
                            WebkitLineClamp: maxLine,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }),
                    }}>
                    {content}
                </Typography>

                {hasOverflow && (
                    <Box
                        component="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            background: `linear-gradient(to right, transparent, ${theme.palette.background.default} 20%)`,
                            paddingLeft: '8px',
                            paddingRight: 0,
                            color: theme.palette.neutralWhite.main,
                            fontWeight: 700,
                            lineHeight: 1.5,
                            fontSize: '14px',
                            border: 'none',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            
                            ...(isExpanded && {
                                top: '100%',
                                background: 'transparent',
                                paddingLeft: 0,
                            }),
                        }}>
                        {isExpanded ? 'voir moins' : '... voir plus'}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
